import { Box, Flex, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { Account, RequireOnly } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { uniqBy } from 'lodash';
import { useMemo, useRef } from 'react';

import { trpc } from '../config/trpc';
import { useCurrentRequest } from '../hooks/useCurrentRequest';
import { useFieldValidationStates } from '../hooks/useFieldValidationStates';
import {
  SimpleContactSearch,
  SimpleContactSearchForm,
  SimpleContactSearchProps,
  SimpleContactSearchSelected,
} from './SimpleContactSearch';

type SimpleAirlineRecipientFormProps = Omit<SimpleContactSearchProps, 'name' | 'children'> & {
  defaultAccount?: RequireOnly<Account, 'id' | 'name'>;
  name: string;
  airportId?: string;
};

const getErrorMessage = (error: any) =>
  Object.values(error)
    .map((value: any) => {
      if (value?.message) return value?.message;
      return undefined;
    })
    .filter(Boolean)
    .join(', ');

const getBorderColor = ({ hasError, hasWarning, isExpanded }) => {
  if (hasError) return 'red.500';
  if (hasWarning) return 'orange.500';
  if (isExpanded) return;
  return 'transparent';
};

export const SimpleCustomerRecipientForm = ({
  name,
  defaultAccount,
  ...contactSearchProps
}: SimpleAirlineRecipientFormProps) => {
  const { error = {}, isInvalid } = useFieldValidationStates(name);
  const errorMessages = getErrorMessage(error);

  const borderColor = getBorderColor({
    hasError: isInvalid,
    hasWarning: false,
    isExpanded: true,
  });
  const searchDisclosure = useDisclosure({ defaultIsOpen: true });

  const scrollToRef = useRef<HTMLDivElement>();

  const { request } = useCurrentRequest();

  const { data: frontContacts, refetch } = trpc.getAssociatedFrontContacts.useQuery({
    request_id: request.id,
  });

  const defaultContacts = useMemo(() => {
    return uniqBy([...(contactSearchProps.defaultSelectedContacts ?? []), ...(frontContacts ?? [])], (x) => x.email);
  }, [frontContacts, contactSearchProps.defaultSelectedContacts]);

  const refreshContactSearch = async () => {
    await refetch();
  };

  return (
    <Flex mb={2} direction="column" borderWidth={1} borderColor={borderColor} borderRadius="lg" pt="6">
      <SimpleContactSearch
        name={name}
        {...contactSearchProps}
        defaultSelectedContacts={defaultContacts}
        defaultAccounts={[defaultAccount]}
        searchPattern="FF"
        refreshContactSearch={refreshContactSearch}
      >
        <Stack p={4} pt={1} spacing="2">
          <SimpleContactSearchSelected />

          <Show if={!!error}>
            <Stack spacing={0} justify="center">
              <Text color="red.500" fontSize="xs">
                {errorMessages}
              </Text>
            </Stack>
          </Show>

          <SimpleContactSearchForm borderWidth={1} p={3} borderRadius="md" hidden={!searchDisclosure.isOpen} />
          <Box ref={scrollToRef} />
        </Stack>
      </SimpleContactSearch>
    </Flex>
  );
};
